import { useMsal } from "@azure/msal-react";
import { DefaultButton, Dropdown, IDropdownOption, IPanelProps, IRenderFunction, Panel, PanelType, PrimaryButton, Separator, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import { RootState } from "app/store";
import LinkTo from "components/controls/LinkTo";
import { EValidationSeverity } from "components/controls/ValidationView/ValidationView.type";
import { emitCustomEvent, fadeIn, panelButtonsStyles } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { ArrayToLookup } from "global/Utility/TSFunctions";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import { IPicked_bcbs_sbc } from "model/CRM.type";
import {
    IGroupNumber as FAM_IGroupNumber
} from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ValidationDialog from "../ValidationDialog";
import { OnRenderDescriptionValidationItem } from "../ValidationDialog/RenderValidationDescription";
import { EValidationState, IValidationModel } from "../ValidationDialog/ValidationDialog.type";
import { ISBCWizardProps } from "./ISBCWizardProps";

interface ISBCWizardValidationData {
    snapshotSBCs: IPicked_bcbs_sbc[];
    planSBC: IPicked_bcbs_sbc | null;
    bcbs_sbc_forward?: string;
}

type TSBCWizardValidationModel = IValidationModel<ISBCWizardValidationData>;

const SBCWizard: React.FC<ISBCWizardProps> = (props) => {

    const { plan } = props;
    const [validation, setValidation] = useState<TSBCWizardValidationModel | null>(null);
    const [viewModel, setViewModel] = useState({
        processing: false,
        finished: false,
        profile: 'new' as 'new' | 'copy',
        sbcId: null as string | null | undefined,
    });

    const state = useSelector(state => state) as RootState;

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    useEffect(() => {
        setViewModel(t => { return { ...t, finished: false, processing: false } });
        setValidation(null);
    }, [props.isOpen]);


    const onConfirm = async (validation: TSBCWizardValidationModel) => {

        validation.validationMessages.sort((b, a) => { return b.type - a.type });

        setValidation(validation);

        if (validation.state === EValidationState.Fulfilled) {
            try {

                setViewModel(t => { return { ...t, processing: true } });

                const SBCGenerateActionImport = await import("global/Application/SBCGenerateAction");

                const { SBCGenerateAction } = SBCGenerateActionImport;

                await SBCGenerateAction(dataSource,
                    props.plan,
                    state,
                    validation.data!.planSBC,
                    validation.data!.bcbs_sbc_forward ?? null
                );

                setViewModel(t => { return { ...t, finished: true } });

            } catch (error) {

                // lets create a new one is payload is not providing one
                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });

            } finally {
                setViewModel(t => { return { ...t, processing: false } });
            }
        }
    }


    const onRenderValidationDialog = () => {
        if (props.isOpen && validation?.state === EValidationState.PendingUser)
            return <ValidationDialog
                defaultValue={validation}
                onCancel={(xValidationModel) => {
                    const validationModel = { ...xValidationModel };
                    validationModel.state = EValidationState.Rejected;
                    setValidation(validationModel);
                }}
                onContinue={(xValidationModel) => {
                    const validationModel = { ...xValidationModel };
                    validationModel.state = EValidationState.Fulfilled;
                    onConfirm(validationModel);
                }}
            />
    }


    const onRenderFinished = () => {
        return <div style={{ justifyContent: 'center', display: 'flex', alignItems: 'center', marginTop: '5%', marginBottom: '100px' }}>
            <Stack tokens={{ childrenGap: '8' }} horizontalAlign={"center"} >
                <Text variant="large" styles={{ root: { fontWeight: '180' } }}>Process is complete.</Text>
            </Stack>
        </div>;
    }

    const profileOptions = useMemo(() => {
        const profileOptions: IDropdownOption<"new" | "copy">[] = [];

        profileOptions.push({
            key: 'new',
            text: 'New',
            data: 'new',
        });

        profileOptions.push({
            key: 'copy',
            text: 'Copy Forward',
            data: 'copy',
        });

        return profileOptions;
    }, []);

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return <div>
            <Separator />
            <Stack>
                {/* <Text>
                    Click continue to generate the SBC.
                </Text> */}
                <Dropdown
                    placeholder="Profile"
                    label="Profile"
                    options={profileOptions}
                    selectedKey={viewModel.profile}
                    disabled={false}
                    onChange={(event, option, index) => {
                        setViewModel(xModel => ({ ...xModel, profile: option?.data ?? 'new' }))

                    }}
                />
            </Stack>
            {
                viewModel.processing &&
                <Spinner
                    label="Please wait..."
                    ariaLive="assertive"
                    labelPosition="right"
                    size={SpinnerSize.large}
                    styles={{ root: { padding: '25px' } }}
                />
            }
            {viewModel.finished && onRenderFinished()}
        </div>;
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return <div className={fadeIn}>
            <PrimaryButton
                text='Continue'
                styles={panelButtonsStyles}
                disabled={viewModel.processing}
                onClick={async (event) => {

                    try {

                        setValidation(null);

                        setViewModel(t => { return { ...t, processing: true } });

                        const snapshotSBCs = await dataSource.getSBC(state.data.snapshot?.bcbs_groupconfigurationsnapshotid!) ?? [];
                        const planSBC = snapshotSBCs.find(q => q.bcbs_planid === plan?.id);

                        const validationModel: IValidationModel<ISBCWizardValidationData> = {
                            validationMessages: [],
                            state: EValidationState.Validating,
                            data: {
                                snapshotSBCs: snapshotSBCs,
                                planSBC: planSBC ?? null
                            }
                        }

                        if ((state.data.modifiedOn ?? -1) > (state.data.savedOn ?? 0)) {
                            validationModel.validationMessages.push({
                                title: `Your changes have not been saved.`,
                                type: EValidationSeverity.warn,
                                description: `An update has been identified that has not been saved to the record. In order to ensure that the latest changes are reflected, it is recommended that you save the update prior to commencing the SBC process.`
                            });
                        }

                        if (planSBC) {
                            if (
                                planSBC?.bcbs_sbctype !== IXrmOptions.bcbs_sbc.bcbs_sbctype['InProgress(1)'].value ||
                                planSBC?.bcbs_sbctype === IXrmOptions.bcbs_sbc.bcbs_sbctype['InProgress(1)'].value
                            ) {
                                if (planSBC?.statuscode === 1) {
                                    validationModel.validationMessages.push({
                                        title: 'This SBC already exists. see details.',
                                        type: EValidationSeverity.error,
                                        onRenderDescription: OnRenderDescriptionValidationItem(
                                            <div>
                                                <div>
                                                    An SBC (<LinkTo sbc={planSBC} />) for current group configuration/snapshot was created before.
                                                </div>

                                            </div>
                                        ),
                                    })
                                }
                            }
                        }

                        // 399549 - SBC - Prevent creation of new SBC on a Configuration that is UWRC
                        // 2: 1, // Underwriting Review Complete -> InActive
                        // 808900001: 1, // Retired -> Inactive
                        // 808900002: 1, // Inactive -> Inactive
                        // 808900004: 1, // MNIL Underwriting Complete -> Inactive
                        // 808900006: 1, // Gold Standard -> Inactive
                        // 808900010: 1, // Merged and Closed -> Inactive
                        // 808900012: 1, // "Transferred to Other LOB(808900012)" -> Inactive
                        // 808900011: 1, // Cancelled -> Inactive
                        // Probably better to use status instead of state code, since not all inactive ones are excluded...
                        // State Codes 0: Active    1: Inactive
                        // if no plans and inactive
                        if (
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Inactive(808900002)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Merged and Closed(808900010)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Transferred to Other LOB(808900012)'].value ||
                            state.data.snapshot.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value
                        ) {
                            validationModel.validationMessages.push({
                                type: EValidationSeverity.error,
                                title: 'SBCs must only be generated for configurations that are open.',
                                description: `The configuration is currently in a state where SBC generation is not permissible according to the established guidelines.`
                            });
                        }

                        // {planId1 : plan1, planId2 : plan2, ...}
                        const planMap = ArrayToLookup(state?.data?.json.plans);

                        // {plan1_id -> [group_number_1, group_number2, ...]}
                        const planGroupNumberLookup: Record<string, FAM_IGroupNumber[]> = {};

                        // Group Structure : Group Number ---
                        state?.data?.json.groupStructure.groupNumbers.forEach(eachGP => {

                            if (eachGP.planRef) {
                                const foundPlan = planMap[eachGP.planRef.id];

                                if (foundPlan && !eachGP.deactivated && !foundPlan.deactivated) {
                                    const t = planGroupNumberLookup[eachGP.planRef.id] ?? [];
                                    t.push(eachGP);
                                    planGroupNumberLookup[eachGP.planRef.id] = t;
                                }
                            }
                        });

                        // SBC - Prevent User from creating SBC when no Subgroups are associated with the Plan.
                        // https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%2015?workitem=372174

                        // SBC - Produce "Warning" when generating a SBC where there are no Subgroups associated to the Plan
                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/322504
                        state?.data?.json?.plans?.forEach(eachPlan => {
                            if (!eachPlan.deactivated)
                                if (!eachPlan.billingOnlyPlan) {
                                    const anyGroupNumber = planGroupNumberLookup[eachPlan.id];

                                    if (!anyGroupNumber) {
                                        validationModel.validationMessages.push({
                                            title: `A Non-Billing Plan (${eachPlan.name}) is missing a related Group Number.`,
                                            description: `All active non-billing plans must have at least one related Group Number.\nIf this is a billing only plan, considering identifying under plan properties.`,
                                            type: EValidationSeverity.error,
                                        });
                                    }
                                }
                        });


                        // Copy Forward for SBC
                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/96852
                        {
                            if (viewModel.profile === 'copy') {

                                // grab all SBCs for current group and plan
                                let bcbs_sbc_forwardList = await dataSource.bcbs_sbcs_forward_getAsync(
                                    state.data.snapshot._bcbs_groupid_value!,
                                    plan.id,
                                )

                                const this_bcbs_effectivedate = (new Date(state.data.snapshot.bcbs_effectivedate!).getTime());

                                // match effective date
                                //bcbs_sbc_forwardList = bcbs_sbc_forwardList.filter(each => {
                                    //const each_bcbs_effectivedate = (new Date(each.bcbs_group_name.bcbs_effectivedate).getTime());
                                    //return each_bcbs_effectivedate === this_bcbs_effectivedate;
                                //})

                                if (bcbs_sbc_forwardList.length < 1) {
                                    validationModel.validationMessages.push({
                                        title: `No previous SBC detected.`,
                                        description: `The system failed to automatically identify and select the most recent SBC that corresponds to the current group, current plan, current group configuration snapshot matching the effective date.`,
                                        type: EValidationSeverity.error,
                                    });
                                } else {
                                    validationModel.data!.bcbs_sbc_forward = bcbs_sbc_forwardList[0].bcbs_sbcid;
                                }

                            }
                        }

                        if (state.status.debug.admin)
                            validationModel.validationMessages.push({
                                title: `Admin mode is on.`,
                                description: `When using admin mode you can continue with saving the data.\nValidations can not prevent bad data submission.`,
                                type: EValidationSeverity.warn,
                            });


                        validationModel.state = validationModel.validationMessages.some(q => !q.resolved)
                            ? EValidationState.PendingUser
                            : EValidationState.Fulfilled

                        await onConfirm(validationModel)

                    } catch (error) {

                    } finally {
                        setViewModel(t => { return { ...t, processing: false } });
                    }

                }}
            />
            <DefaultButton
                text='Cancel'
                onClick={props.onDismiss}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    return <>
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            isLightDismiss={false}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="SBC Generation"
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
            {onRenderValidationDialog()}

        </Panel>
    </>
}

export default SBCWizard